// Main application logic
import 'bulma/css/bulma.css';
import '@fortawesome/fontawesome-free/css/all.css';
import './style.css';
import { parseMarkdown } from './parser.js';
import { downloadAnkiPackage } from './apkg-generator.js';

// State
let cards = [];
let selectedIndex = 0;
let currentTab = 'front';
let templates = {
    front: '',
    back: '',
    css: ''
};

// Local Storage Keys
const DECK_NAME_STORAGE_KEY = 'md2anki_deck_name';
const REVERSED_DECK_NAME_STORAGE_KEY = 'md2anki_reversed_deck_name';

// Elements
const fileInput = document.getElementById('fileInput');
const fileName = document.getElementById('fileName');
const mainContent = document.getElementById('mainContent');
const cardCount = document.getElementById('cardCount');
const cardList = document.getElementById('cardList');
const previewContainer = document.getElementById('previewContainer');
const deckNameInput = document.getElementById('deckNameInput');
const reversedDeckNameInput = document.getElementById('reversedDeckNameInput');

const tabs = document.querySelectorAll('.tabs li');

// Init
async function init() {
    try {
        const [frontResponse, backResponse, cssResponse] = await Promise.all([
            fetch('card-template/anki_front.html'),
            fetch('card-template/anki_back.html'),
            fetch('card-template/anki.css')
        ]);
        if (frontResponse.ok) templates.front = await frontResponse.text();
        if (backResponse.ok) templates.back = await backResponse.text();
        if (cssResponse.ok) templates.css = await cssResponse.text();
    } catch (e) {
        console.error('Failed to load templates', e);
        // Fallback or just log. The copy buttons might be empty, and preview unstyled/default.
    }
    loadDeckNames();
}
init();

// Deck name persistence
function loadDeckNames() {
    const savedName = localStorage.getItem(DECK_NAME_STORAGE_KEY);
    if (savedName) deckNameInput.value = savedName;

    const savedReversedName = localStorage.getItem(REVERSED_DECK_NAME_STORAGE_KEY);
    if (savedReversedName) reversedDeckNameInput.value = savedReversedName;
}

function setDeckNameFromFile(filename) {
    // Remove .md extension to get default deck name
    const defaultName = filename.replace(/\.md$/i, '');

    // Check if we have a saved name in local storage
    const savedName = localStorage.getItem(DECK_NAME_STORAGE_KEY);
    deckNameInput.value = savedName || defaultName;

    // Handle reversed name default
    const savedReversedName = localStorage.getItem(REVERSED_DECK_NAME_STORAGE_KEY);
    reversedDeckNameInput.value = savedReversedName || `${deckNameInput.value} (Reversed)`;
}

// Save deck name when changed
deckNameInput.addEventListener('change', () => {
    const name = deckNameInput.value.trim();
    if (name) {
        localStorage.setItem(DECK_NAME_STORAGE_KEY, name);
    }
});
deckNameInput.addEventListener('blur', () => {
    const name = deckNameInput.value.trim();
    if (name) {
        localStorage.setItem(DECK_NAME_STORAGE_KEY, name);
    }
});

// Save reversed deck name when changed
reversedDeckNameInput.addEventListener('change', () => {
    const name = reversedDeckNameInput.value.trim();
    if (name) {
        localStorage.setItem(REVERSED_DECK_NAME_STORAGE_KEY, name);
    }
});
reversedDeckNameInput.addEventListener('blur', () => {
    const name = reversedDeckNameInput.value.trim();
    if (name) {
        localStorage.setItem(REVERSED_DECK_NAME_STORAGE_KEY, name);
    }
});

// File Input
fileInput.addEventListener('change', async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    fileName.textContent = file.name;
    const text = await file.text();
    cards = parseMarkdown(text);

    // Set deck name from file (or use saved value)
    setDeckNameFromFile(file.name);

    cardCount.textContent = cards.length;
    renderList();
    if (cards.length > 0) {
        selectedIndex = 0;
        mainContent.style.display = ''; // Remove display: none (default defaults to block/flex as defined in css or user agent)
        // Actually .columns is flex, so removing inline display: none restores it.
        updateActiveItem();
        renderPreview();
    }
});

function renderList() {
    cardList.innerHTML = '';
    cards.forEach((card, index) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.textContent = card.phrase || '(No Phrase)';
        a.onclick = () => {
            selectedIndex = index;
            updateActiveItem();
            renderPreview();
        };
        li.appendChild(a);
        cardList.appendChild(li);
    });
}

function updateActiveItem() {
    const items = cardList.querySelectorAll('a');
    items.forEach((item, index) => {
        if (index === selectedIndex) item.classList.add('is-active');
        else item.classList.remove('is-active');
    });
}

// Shadow DOM for Preview
const shadow = previewContainer.attachShadow({ mode: 'open' });

function renderPreview() {
    if (cards.length === 0) return;
    const card = cards[selectedIndex];

    // Inject CSS
    const style = document.createElement('style');
    style.textContent = templates.css;

    // Render HTML
    let html = '';
    const renderFront = (card) => {
        return templates.front.replace(/{{Phrase}}/g, card.phrase || '');
    };

    if (currentTab === 'front') {
        html = renderFront(card);
    } else {
        html = templates.back
            .replace(/{{FrontSide}}/g, renderFront(card))
            .replace(/{{Translation}}/g, card.translation || '')
            .replace(/{{Example}}/g, card.example || '')
            .replace(/{{ExampleTranslation}}/g, card.exampleTranslation || '');
    }

    shadow.innerHTML = '';
    shadow.appendChild(style);
    const wrapper = document.createElement('div');
    // Basic Anki wrapper simulation
    wrapper.innerHTML = html;
    shadow.appendChild(wrapper);
}

// Tabs
tabs.forEach(tab => {
    tab.addEventListener('click', (e) => {
        // Find the li element even if clicked on a child <a>
        const li = e.currentTarget;
        tabs.forEach(tab => tab.classList.remove('is-active'));
        li.classList.add('is-active');
        currentTab = li.dataset.tab;
        renderPreview();
    });
});

const downloadApkgBtn = document.getElementById('downloadApkgBtn');
const downloadReversedApkgBtn = document.getElementById('downloadReversedApkgBtn');

async function handleDownload(reversed = false, button) {
    if (cards.length === 0) {
        alert('No cards to export!');
        return;
    }

    const originalText = button.textContent;
    button.textContent = 'Generating...';
    button.setAttribute('disabled', 'true');

    try {
        let deckName = '';
        if (reversed) {
            deckName = reversedDeckNameInput.value.trim() || `${deckNameInput.value.trim()} (Reversed)`;
        } else {
            deckName = deckNameInput.value.trim() || 'Markdown2Anki Deck';
        }
        
        await downloadAnkiPackage(cards, templates, deckName, reversed);
    } catch (e) {
        console.error(e);
        alert('Failed to generate Anki package. Check console for details.');
    } finally {
        button.textContent = originalText;
        button.removeAttribute('disabled');
    }
}

downloadApkgBtn.addEventListener('click', () => handleDownload(false, downloadApkgBtn));
downloadReversedApkgBtn.addEventListener('click', () => handleDownload(true, downloadReversedApkgBtn));



console.log('App initialized');
